<template>
  <div class="ren-decoration-template-market">
    <header class="ren-decoration-template-page__head">
      <div class="ren-decoration-template-page__head-left">
        <RrButton size="small" type="default" @click="$emit('back')">
          ← 返回组件库
        </RrButton>
        <div class="ren-decoration-market-segmented" role="tablist" aria-label="模板切换">
          <button type="button" :class="{ active: !recycleMode }" @click="showMarket">模板市场</button>
          <button type="button" :class="{ active: recycleMode }" @click="showRecycle">回收站</button>
        </div>
      </div>
      <div class="ren-decoration-template-page__head-actions">
        <RrButton type="default" title="刷新" @click="$emit('refresh')">刷新</RrButton>
        <RrButton
          v-if="!recycleMode && templateActionsEnabled"
          type="primary"
          :disabled="readonly"
          @click="$emit('create-template')">
          从当前页创建模板
        </RrButton>
      </div>
    </header>

    <div class="ren-decoration-template-toolbar">
      <RrInput
        v-model:value.trim="templateKeyword"
        placeholder="搜索模板名称 / 编码 / 描述"
        allow-clear
        style="width: 240px;" />
      <RrSelect
        v-model:value="selectedTemplateScope"
        :disabled="recycleMode"
        :options="templateScopeOptions"
        style="width: 120px;" />
      <RrSelect
        v-if="!recycleMode"
        v-model:value="selectedTemplateStatus"
        :options="templateStatusOptions"
        style="width: 110px;" />
      <RrSelect v-model:value="selectedTemplateType" :options="allTemplateTypeOptions" style="width: 120px;" />
      <RrSelect v-model:value="selectedPageType" :options="allPageTypeOptions" style="width: 120px;" />
      <RrButton type="default" style="margin-left: auto;" @click="resetFilters">重置</RrButton>
    </div>

    <div v-if="pagedTemplates.length" class="ren-decoration-template-grid">
      <article
        v-for="template in pagedTemplates"
        :key="template.templateCode"
        class="ren-decoration-market-card"
        :class="{ 'is-disabled': !templateEnabled(template) }">
        <button
          class="ren-decoration-market-card__preview"
          :style="{ background: templateBackgroundColor(template) }"
          type="button"
          @click="$emit('open-detail', template.templateCode)">
          <img v-if="template.coverImageUrl" :alt="template.templateName" :src="template.coverImageUrl" />
          <template v-else>
            <div class="ren-decoration-market-card__preview-bar">
              <span></span>
              <strong>{{ templatePreviewTitle(template) }}</strong>
              <span></span>
            </div>
            <div class="ren-decoration-market-card__hero" :style="{ background: templateThemeColor(template) }">
              <strong>{{ templatePreviewTitle(template) }}</strong>
              <span>{{ template.description || template.templateCode }}</span>
            </div>
            <div class="ren-decoration-market-card__blocks">
              <span v-for="component in templatePreviewComponents(template)" :key="component.id">
                {{ componentPreviewLabel(component) }}
              </span>
            </div>
          </template>
        </button>

        <div class="ren-decoration-market-card__body">
          <div class="ren-decoration-market-card__tags">
            <RrTag>{{ templateScopeLabel(template) }}</RrTag>
            <RrTag :type="templateEnabled(template) ? 'success' : 'info'">{{ templateStatusLabel(template) }}</RrTag>
          </div>
          <h4>{{ template.templateName }}</h4>
          <p>{{ pageTypeLabel(template.pageType) }}</p>
          <p>备注：{{ template.description || template.templateCode }}</p>
          <dl>
            <div>
              <dt>更新时间</dt>
              <dd>{{ formatTemplateDate(template.updatedAt || '') }}</dd>
            </div>
          </dl>
        </div>

        <footer class="ren-decoration-market-card__actions">
          <template v-if="isRecycledTemplate(template)">
            <RrButton
              v-if="templateActionsEnabled"
              type="default"
              :disabled="readonly || template.publicTemplate"
              @click="$emit('restore-template', template)">
              恢复
            </RrButton>
            <RrPopconfirm
              v-if="templateActionsEnabled"
              title="确定永久删除此模板？"
              description="删除后无法恢复"
              ok-text="永久删除"
              ok-type="primary"
              :ok-button-props="{ danger: true }"
              cancel-text="取消"
              :disabled="readonly || template.publicTemplate"
              @confirm="$emit('delete-permanently', template)">
              <RrButton type="primary" danger :disabled="readonly || template.publicTemplate">永久删除</RrButton>
            </RrPopconfirm>
          </template>
          <template v-else>
            <RrButton type="default" :disabled="!templateEnabled(template)" @click="$emit('open-detail', template.templateCode)">
              预览
            </RrButton>
            <RrButton
              v-if="templateActionsEnabled && !template.publicTemplate"
              type="default"
              :disabled="readonly"
              @click="$emit('edit-template', template)">
              编辑
            </RrButton>
            <RrButton
              v-if="templateActionsEnabled"
              type="default"
              :disabled="readonly"
              @click="$emit('copy-template', template)">
              复制
            </RrButton>
            <RrButton
              v-if="templateActionsEnabled && !template.publicTemplate"
              type="default"
              :disabled="readonly"
              @click="$emit('toggle-status', template)">
              {{ templateEnabled(template) ? '禁用' : '启用' }}
            </RrButton>
            <RrPopconfirm
              v-if="templateActionsEnabled && !template.publicTemplate"
              title="确定将此模板移入回收站？"
              ok-text="移入回收站"
              ok-type="primary"
              :ok-button-props="{ danger: true }"
              cancel-text="取消"
              :disabled="readonly"
              @confirm="$emit('delete-template', template)">
              <RrButton type="default" :disabled="readonly">回收</RrButton>
            </RrPopconfirm>
          </template>
        </footer>
      </article>
    </div>

    <RrEmpty v-else title="暂无符合条件的模板" compact />

    <div v-if="filteredTemplates.length" class="ren-decoration-template-pagination-wrapper">
      <RrPagination
        v-model:current="templateCurrentPage"
        v-model:page-size="templatePageSize"
        :total="filteredTemplates.length"
        :page-size-options="[6, 9, 12, 24]"
        show-size-changer
        show-quick-jumper
        show-total />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RrButton, RrEmpty, RrInput, RrPagination, RrPopconfirm, RrSelect, RrTag } from '@xjlv2/ui'
import type { RenDecorationComponent, RenDecorationMarketTemplate } from '../../types'
import {
  formatTemplateDate,
  isRecycledTemplate,
  normalizeSearch,
  normalizeUpperText,
  pageTypeLabel,
  parseTemplateSchema,
  templateEnabled,
  templateScopeLabel,
  templateStatusLabel,
  templateTypeLabel,
  uniqueSorted,
} from '../utils/template-market'

const props = withDefaults(
  defineProps<{
    templates?: RenDecorationMarketTemplate[]
    readonly?: boolean
    templateActionsEnabled?: boolean
  }>(),
  {
    templates: () => [],
    readonly: false,
    templateActionsEnabled: true,
  },
)

defineEmits<{
  back: []
  refresh: []
  'create-template': []
  'open-detail': [templateCode: string]
  'edit-template': [template: RenDecorationMarketTemplate]
  'copy-template': [template: RenDecorationMarketTemplate]
  'toggle-status': [template: RenDecorationMarketTemplate]
  'restore-template': [template: RenDecorationMarketTemplate]
  'delete-template': [template: RenDecorationMarketTemplate]
  'delete-permanently': [template: RenDecorationMarketTemplate]
}>()

const recycleMode = ref(false)
const templateKeyword = ref('')
const selectedTemplateType = ref('all')
const selectedPageType = ref('all')
const selectedTemplateScope = ref<'all' | 'system' | 'tenant'>('all')
const selectedTemplateStatus = ref<'all' | 'active' | 'disabled'>('active')
const templateCurrentPage = ref(1)
const templatePageSize = ref(6)

const templateScopeOptions = [
  { label: '全部来源', value: 'all' },
  { label: '系统模板', value: 'system' },
  { label: '我的模板', value: 'tenant' },
]
const templateStatusOptions = [
  { label: '全部状态', value: 'all' },
  { label: '可用', value: 'active' },
  { label: '已停用', value: 'disabled' },
]

const templateTypeOptions = computed(() =>
  uniqueSorted(props.templates.map((template) => normalizeUpperText(template.templateType)).filter(Boolean)),
)
const pageTypeOptions = computed(() =>
  uniqueSorted(props.templates.map((template) => normalizeUpperText(template.pageType)).filter(Boolean)),
)
const allTemplateTypeOptions = computed(() => [
  { label: '全部类型', value: 'all' },
  ...templateTypeOptions.value.map((type) => ({ label: templateTypeLabel(type), value: type })),
])
const allPageTypeOptions = computed(() => [
  { label: '全部页面', value: 'all' },
  ...pageTypeOptions.value.map((type) => ({ label: pageTypeLabel(type), value: type })),
])

watch(
  [recycleMode, templateKeyword, selectedTemplateScope, selectedTemplateStatus, selectedTemplateType, selectedPageType],
  () => {
    templateCurrentPage.value = 1
  },
)

function resetFilters() {
  templateKeyword.value = ''
  selectedTemplateType.value = 'all'
  selectedPageType.value = 'all'
  selectedTemplateScope.value = 'all'
  selectedTemplateStatus.value = 'active'
  templateCurrentPage.value = 1
}

function showMarket() {
  recycleMode.value = false
  selectedTemplateScope.value = 'all'
}

function showRecycle() {
  recycleMode.value = true
  selectedTemplateScope.value = 'tenant'
}

const filteredTemplates = computed(() => {
  const keyword = normalizeSearch(templateKeyword.value)
  return props.templates.filter((template) => {
    const templateType = normalizeUpperText(template.templateType)
    const pageType = normalizeUpperText(template.pageType)
    const recycled = isRecycledTemplate(template)
    const matchesTemplateType = selectedTemplateType.value === 'all' || templateType === selectedTemplateType.value
    const matchesPageType = selectedPageType.value === 'all' || pageType === selectedPageType.value
    const matchesRecycle = recycleMode.value ? recycled : !recycled
    const matchesScope =
      selectedTemplateScope.value === 'all' ||
      (selectedTemplateScope.value === 'system' && template.publicTemplate) ||
      (selectedTemplateScope.value === 'tenant' && !template.publicTemplate)
    const matchesStatus =
      recycleMode.value ||
      selectedTemplateStatus.value === 'all' ||
      (selectedTemplateStatus.value === 'active' && templateEnabled(template)) ||
      (selectedTemplateStatus.value === 'disabled' && !templateEnabled(template) && !recycled)
    const matchesKeyword =
      !keyword ||
      [
        template.templateName,
        template.templateCode,
        template.description,
        template.moduleCode,
        template.templateType,
        template.pageType,
      ].some((value) => normalizeSearch(value).includes(keyword))
    return matchesTemplateType && matchesPageType && matchesRecycle && matchesScope && matchesStatus && matchesKeyword
  })
})

const pagedTemplates = computed(() => {
  const start = (templateCurrentPage.value - 1) * templatePageSize.value
  return filteredTemplates.value.slice(start, start + templatePageSize.value)
})

function schemaForTemplate(template: RenDecorationMarketTemplate) {
  return parseTemplateSchema(template.schemaJson)
}

function templatePreviewTitle(template: RenDecorationMarketTemplate) {
  return schemaForTemplate(template)?.page.title || template.templateName
}

function templatePreviewComponents(template: RenDecorationMarketTemplate, limit = 4) {
  return (schemaForTemplate(template)?.components ?? []).filter((component) => component.enabled !== false).slice(0, limit)
}

function templateThemeColor(template: RenDecorationMarketTemplate) {
  return schemaForTemplate(template)?.page.themeColor || '#5A46E0'
}

function templateBackgroundColor(template: RenDecorationMarketTemplate) {
  return schemaForTemplate(template)?.page.backgroundColor || '#F3F5F7'
}

function componentPreviewLabel(component: RenDecorationComponent) {
  return String(component.props?.title || component.title || component.type).slice(0, 6)
}
</script>
